import { ref, type Ref } from 'vue';
import type { AnchorPoint, HAlign, VAlign, CssPosition } from './types';
import { buildPositionStyles } from './utils/buildPositionStyles';
import { calcLeft } from './utils/calcLeft';
import { calcTop } from './utils/calcTop';


const WINDOW_GAP = 5;

/**
 * Computes and tracks the position of a floating menu relative to a trigger
 * element, with single-pass flip logic for viewport overflow.
 *
 * When the computed position overflows the viewport on a given axis, both
 * anchor points on that axis are flipped once (e.g. `top` ↔ `bottom`,
 * `left` ↔ `right`) and the position is recalculated. If the flipped
 * position still overflows, it is kept as-is.
 *
 * @param triggerEl - Ref to the trigger element.
 * @param menuEl - Ref to the menu element.
 * @param triggerAnchor - The active trigger anchor point.
 * @param menuAnchor - The active menu anchor point.
 * @param offset - Optional gap in pixels applied only along the axis where the
 *   trigger and menu have touching edges (e.g. trigger's `bottom` meets menu's
 *   `top`). Center-aligned axes are not affected. After flip the touching edges
 *   are re-evaluated on the final anchors, so the gap always points away from
 *   the trigger.
 * @param isRelativeToTrigger - CSS positioning strategy for the menu element.
 *   `false` - places the menu relative to the viewport;
 *   `true` - places the menu relative to the trigger element's top-left corner (the
 *   trigger must establish a positioned context).
 * @returns Reactive menu position state and imperative helpers.
 */
export const useAnchorPositioning = (
    triggerEl: Ref<HTMLElement | null>,
    menuEl: Ref<HTMLElement | null>,
    triggerAnchor: AnchorPoint,
    menuAnchor: AnchorPoint,
    offset?: number,
    isRelativeToTrigger: boolean = false
) => {
    const menuStyle = ref<CssPosition>(buildPositionStyles());
    const isPositioned = ref(false);

    const calcPosition = () => {
        if (!triggerEl.value || !menuEl.value) return;


        // props

        const triggerRect = triggerEl.value.getBoundingClientRect();
        const menuRect = menuEl.value.getBoundingClientRect();

        let [triggerV, triggerH] = triggerAnchor.split('-') as [VAlign, HAlign];
        let [menuV, menuH] = menuAnchor.split('-') as [VAlign, HAlign];

        const { width, height } = menuRect;
        const windowWidth = document.documentElement.clientWidth;
        const windowHeight = document.documentElement.clientHeight;


        // process position

        let left = calcLeft(triggerRect, menuRect, triggerH, menuH);
        let top = calcTop(triggerRect, menuRect, triggerV, menuV);

        // flip horizontal once if the menu overflows left or right
        if (left < WINDOW_GAP) {
            if (triggerH === 'left' && menuH === 'right') {
                triggerH = 'right';
                menuH = 'left';
                left = calcLeft(triggerRect, menuRect, triggerH, menuH);
            }
            else {
                left = WINDOW_GAP;
            }
        }
        else if (left + width > windowWidth - WINDOW_GAP) {
            if (triggerH === 'right' && menuH === 'left') {
                triggerH = 'left';
                menuH = 'right';
                left = calcLeft(triggerRect, menuRect, triggerH, menuH);
            }
            else {
                left = windowWidth - WINDOW_GAP - width;
            }
        }

        // flip vertical once if the menu overflows top or bottom
        if (top < WINDOW_GAP) {
            if (triggerV === 'top' && menuV === 'bottom') {
                triggerV = 'bottom';
                menuV = 'top';
                top = calcTop(triggerRect, menuRect, triggerV, menuV);
            }
            else {
                top = WINDOW_GAP;
            }
        }
        else if (top + height > windowHeight - WINDOW_GAP) {
            if (triggerV === 'bottom' && menuV === 'top') {
                triggerV = 'top';
                menuV = 'bottom';
                top = calcTop(triggerRect, menuRect, triggerV, menuV);
            }
            else {
                top = windowHeight - WINDOW_GAP - height;
            }
        }

        // apply gap only where touching edges exist (evaluated on final anchors after flip)
        if (offset) {
            if (triggerV === 'bottom' && menuV === 'top') top += offset;
            else if (triggerV === 'top' && menuV === 'bottom') top -= offset;

            if (triggerH === 'right' && menuH === 'left') left += offset;
            else if (triggerH === 'left' && menuH === 'right') left -= offset;
        }


        // calc absolute postioning related to triggerEl's 0/0
        if (isRelativeToTrigger) {
            top  -= triggerRect.top;
            left -= triggerRect.left;
        }

        // set state
        menuStyle.value = buildPositionStyles(top, left);
        isPositioned.value = true;
    };

    const resetPosition = () => {
        isPositioned.value = false;
    };


    return {
        menuStyle,
        isPositioned,
        calcPosition,
        resetPosition,
    };
}
